import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Chip from "../../Chip/Chip";
import CustomSearch from "../../FormFields/Custom/CustomSearch";

const ColumnSelector = ({
  columns = [],
  selectedColumns = [],
  onChange,
  placeholder = "Select columns",
  size = "md",
  rounded = "rounded",
  disabled = false,
  showSearch = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(selectedColumns || []);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownContainerRef = useRef(null);
  const initialRenderRef = useRef(true);

  // Size styles
  const sizeClasses = {
    xs: "px-2 py-0",
    "xs-sm": "px-2.5 py-1",
    sm: "px-3 py-1.5",
    md: "px-3 py-2",
    lg: "px-4 py-2.5",
    xl: "px-5 py-3",
  };

  const fontSizeClasses = {
    xs: "text-[12px]",
    "xs-sm": "text-[12px]",
    sm: "text-[12px]",
    md: "text-[14px]",
    lg: "text-[16px]",
    xl: "text-[18px]",
  };

  const iconSizeClasses = {
    xs: "!text-[12px]",
    "xs-sm": "!text-[14px]",
    sm: "!text-[14px]",
    md: "!text-[14px]",
    lg: "!text-[15px]",
    xl: "!text-[15px]",
  };

  // Initialize selected columns from props only once on mount or when selectedColumns changes
  useEffect(() => {
    if (selectedColumns && Array.isArray(selectedColumns)) {
      setSelected(selectedColumns);
    }
  }, [JSON.stringify(selectedColumns)]); // Use JSON.stringify to compare arrays

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (dropdownContainerRef.current &&
          dropdownContainerRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearch("");
    }
  }, [isOpen]);

  // Position dropdown
  useEffect(() => {
    if (isOpen && buttonRef.current && dropdownContainerRef.current) {
      const updatePosition = () => {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownRect =
          dropdownContainerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Calculate available space
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        // Determine if dropdown should be below or above button
        const showBelow =
          spaceBelow >= Math.min(320, dropdownRect.height) ||
          spaceBelow >= spaceAbove;

        // Calculate left position
        let left = buttonRect.left;
        // Adjust if dropdown would overflow right edge
        if (left + dropdownRect.width > viewportWidth) {
          left = Math.max(0, viewportWidth - dropdownRect.width);
        }

        // Calculate top position
        let top;
        if (showBelow) {
          top = buttonRect.bottom + 8; // Add 8px gap
          // Check if dropdown extends beyond bottom of viewport
          if (top + dropdownRect.height > viewportHeight) {
            top = viewportHeight - dropdownRect.height - 8;
          }
        } else {
          top = buttonRect.top - dropdownRect.height - 8; // Add 8px gap
          // Check if dropdown extends beyond top of viewport
          if (top < 0) {
            top = 8;
          }
        }

        // Apply the calculated position
        dropdownContainerRef.current.style.left = `${left}px`;
        dropdownContainerRef.current.style.top = `${top}px`;
      };

      // Initial position update
      updatePosition();

      // Update position on scroll
      const handleScroll = () => {
        requestAnimationFrame(updatePosition);
      };

      // Add scroll event listeners
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);

      // Cleanup
      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [isOpen]);

  // Check if a column is selected - Fixed function
  const isColumnSelected = (column) => {
    if (!column || !column.key) return false;

    return selected.some((item) => item && item.key === column.key);
  };

  // Filter columns based on search and exclude already selected columns
  const filteredColumns = columns.filter((column) => {
    if (!column || typeof column.label !== "string") return false;

    // Skip columns that are already selected
    if (isColumnSelected(column)) return false;

    const searchTerm = search || "";
    if (searchTerm.trim() === "") {
      return true;
    }
    return column.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Generate a unique key for each column
  const getUniqueKey = (column, index) => {
    if (!column) return `column-${index}`;
    if (column.key) return `column-${column.key}-${index}`;
    if (column.label) return `column-${column.label}-${index}`;
    return `column-${index}`;
  };

  // Toggle column selection - Fixed function
  const handleColumnSelect = (column) => {
    if (!column || !column.key) return;

    const isSelected = isColumnSelected(column);

    let updatedSelection;
    if (isSelected) {
      updatedSelection = selected.filter(
        (item) => item && item.key !== column.key
      );
    } else {
      updatedSelection = [...selected, column];
    }

    setSelected(updatedSelection);

    if (onChange) {
      onChange(updatedSelection);
    }
  };

  // Remove a selected column
  const handleRemoveColumn = (columnToRemove) => {
    const newSelected = selected.filter(
      (column) => column && column.key !== columnToRemove.key
    );
    setSelected(newSelected);
    if (onChange) {
      onChange(newSelected);
    }
  };

  // Handle drag start
  const handleDragStart = (index) => {
    setDraggingIndex(index);
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === index) return;

    const newSelected = [...selected];
    const draggedItem = newSelected[draggingIndex];

    // Remove the dragged item
    newSelected.splice(draggingIndex, 1);
    // Insert it at the new position
    newSelected.splice(index, 0, draggedItem);

    setSelected(newSelected);
    setDraggingIndex(index);

    if (onChange) {
      onChange(newSelected);
    }
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggingIndex(null);
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`w-full border border-gray-300 flex justify-between items-center hover:border-primary focus:border-primary focus:outline-none ${
          sizeClasses[size]
        } ${fontSizeClasses[size]} ${rounded} ${
          disabled
            ? "bg-gray-200 cursor-not-allowed text-gray-600"
            : "bg-white cursor-pointer"
        }`}
        onClick={toggleDropdown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        ref={buttonRef}
      >
        <div
          className={`line-clamp-1 text-left flex-1 overflow-hidden ${
            selected && selected.length > 0 ? "text-black" : "text-neutrals-500"
          }`}
        >
          {/* Selected columns display - Now inside the dropdown */}
          {selected && selected.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selected.map((column, index) => (
                <div
                  key={getUniqueKey(column, index)}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className="cursor-move"
                >
                  <Chip
                    color="primary"
                    label={column.label}
                    onDelete={(e) => {
                      e.stopPropagation(); // Stop event propagation to prevent dropdown toggle
                      handleRemoveColumn(column);
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            placeholder
          )}
        </div>
        <span
          className={`material-icons transition-transform ${iconSizeClasses[size]} ml-1 flex-shrink-0`}
        >
          {isOpen ? "expand_less" : "expand_more"}
        </span>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownContainerRef}
            style={{
              position: "fixed",
              width: buttonRef.current?.offsetWidth,
            }}
            className="z-[55] bg-white border border-gray-200 rounded-lg shadow-lg"
          >
            {showSearch && (
              <div className="p-1 border-b border-gray-200">
                <CustomSearch
                  placeholder="Search columns..."
                  value={search}
                  onChange={(key, value) => setSearch(value)}
                  size={size}
                  rounded="rounded-full"
                  keyname="search"
                />
              </div>
            )}

            <div className="max-h-48 overflow-y-auto">
              {filteredColumns.length > 0 ? (
                <ul>
                  {filteredColumns.map((column, index) => {
                    // Pre-compute selection status for clarity
                    const isSelected = isColumnSelected(column);

                    return (
                      <li
                        key={getUniqueKey(column, index)}
                        className={`p-2 flex justify-between items-center cursor-pointer ${
                          isSelected
                            ? "bg-primary-50 hover:bg-primary-100"
                            : "hover:bg-primary-50"
                        }`}
                        onClick={() => handleColumnSelect(column)}
                      >
                        <span className="truncate">{column.label}</span>
                        {isSelected && (
                          <span className="material-icons !text-[16px]">
                            check
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="p-2 text-gray-500 text-center">
                  No columns found
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ColumnSelector;
